import { COUNTDOWN_DAY_MS, countdownClock } from './countdown.js';

/*
 * When an item leaves the shop, in words (ENG-876): shared by SkinTimeLimit
 * and by a sale line that ends with the item's window (SkinPriceTag
 * `windowEnds`). The live clock is hooks/useCountdown.js; this is the pure
 * part.
 */

/**
 * How long before an item leaves the shop its date gives way to a live
 * countdown: its last 24 hours, the 24-hour mark included.
 */
export const LEAVING_COUNTDOWN_MS = COUNTDOWN_DAY_MS;

/**
 * The line for an item leaving at `until` (a Date, RFC 3339 string or ms):
 *
 *   { text, spoken, label, countdown }
 *
 * - More than 24 hours out: "Leaving Apr 1, 2027", in `format`'s time zone
 *   (the viewer's own unless the formatters name one).
 * - In the last 24 hours: a clock, "Leaving in 05:12:09" (countdownClock;
 *   "05:12" without `seconds`, when the clock ticks by the minute), with
 *   a coarser "in 5 hours" for screen readers, so nothing is read out
 *   every second. `countdown` is true.
 * - `ended`: "No longer available".
 *
 * `spoken` is a fragment for a longer sentence ("leaving Apr 1, 2027",
 * "leaving in 5 hours", "no longer available": the sale price's label);
 * `label` is the line on its own for a screen reader, "Leaving in 5 hours".
 *
 * `remainingMs` is the clock's (useCountdown: the absolute end minus the
 * device clock); without it, it is read from Date.now().
 */
export const leavingText = (t, format, until, { remainingMs, ended = false, seconds = false } = {}) => {
  if (ended) {
    const text = t('shop.availability.ended');
    return { text, spoken: t('shop.availability.ariaEnded'), label: text, countdown: false };
  }
  const endMs = until instanceof Date ? until.getTime() : typeof until === 'number' ? until : Date.parse(until);
  const remaining = Number.isFinite(remainingMs) ? remainingMs : Math.max(0, endMs - Date.now());
  if (remaining <= LEAVING_COUNTDOWN_MS) {
    const inWords = format.relativeTime(endMs, { now: endMs - remaining, numeric: 'always' });
    return {
      text: t('shop.availability.leavingIn', { time: countdownClock(format, remaining, { seconds }) }),
      spoken: t('shop.availability.ariaLeavingIn', { time: inWords }),
      label: t('shop.availability.leavingInLabel', { time: inWords }),
      countdown: true,
    };
  }
  const date = format.date(endMs, 'short');
  const text = t('shop.availability.leaving', { date });
  return { text, spoken: t('shop.availability.ariaLeaving', { date }), label: text, countdown: false };
};
